/*
 * ContractMapping.tsx
 * Author: fwunderlich
 * Date: 18.04.2024
 *
 * Copyright: DMT GmbH & Co. KG
 */

import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    useGetContractQuery,
    useGetProjectContractTypesQuery
} from '../../../redux/rtk/injectedContractApi';
import {addMappings, Mapping, MappingType, removeMapping} from '../../../redux/slices/MappingSlice';
import {useAppDispatch, useAppSelector} from '../../../redux/hooks';
import CustomDataTable, {ColumnObject} from '../../global/CustomDataTable';
import {
    getColumnsContract,
    getFiltersContract,
    Contract,
    getMappingContent,
    assignMappingGlobal, getColumnsParcel
} from '../../functions/GlobalData';
import {CustomConfirmDialog} from '../../global/CustomConfirmDialog';
import {checkPermission} from '../../../functions/functionLibrary';
import {permissions} from '../../../config/permissions';
import CustomDialog from '../../global/CustomDialog';
import {useGetPersonenTypesQuery} from '../../../redux/rtk/injectedMappingApi';
import {Button} from 'primereact/button';
import {useNavigate} from 'react-router-dom';
import {faMap} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {OverlayAddMapping} from '../../globalData/OverlayAddMapping';

type Props = {
    projectId: string,
    mappingId: string,
    mappingType: MappingType,
    onChange: () => void,
    className?: string,
    scrollHeight?: string,
    showHeader?: boolean,
    skip?: boolean,
    isDirty?: boolean
}

/**
 * Form for editing and creating of users
 * @param props
 * @constructor
 */
export const ContractMapping = (props: Props): JSX.Element => {

    const {t} = useTranslation(['common', 'globalData', 'toasts']);

    const navigate = useNavigate();

    const mappings: Mapping[] = useAppSelector((state: any) => state.mappingData.mappings);
    const dispatch = useAppDispatch();

    const [values, setValues] = useState<Contract[]>([]);
    const [selectedValue, setSelectedValue] = useState<Contract | undefined>(undefined);
    const [isDirty, setIsDirty] = useState<any>(props.isDirty);
    const [dataLoaded, setDataLoaded] = useState<boolean>(false);
    const [showMappingAdd, setShowMappingAdd] = useState<boolean>(false);
    const [showMappingEdit, setShowMappingEdit] = useState<boolean>(false);
    const [columns, setColumns] = useState<ColumnObject[]>([]);
    const [customButtons, setCustomButtons] = useState<any[]>([]);
    const [activeMappings, setActiveMappings] = useState<any[]>([]);
    const [mappingDialog, setMappingDialog] = useState<any>(undefined);
    const [mappingInputs, setMappingInputs] = useState<any>({});
    const [hasValidationError, setHasValidationError] = useState<boolean>(false);

    const getMapping = () => {
        switch (props.mappingType) {
            case MappingType.parcelContract:
                return {flurstueck_id: props.mappingId};
            default:
                return undefined;
        }
    };

    const contractTypesQuery = useGetProjectContractTypesQuery({
        projectId: props.projectId,
        flurstueck_id: ''
    });

    const {
        data: contracts = [], isLoading,
        isSuccess,
        isFetching,
        isError,
        error,
        refetch,
    } = useGetContractQuery({
        projectId: props.projectId,
        flurstueckId: props.mappingId,
    }, {skip: props.skip != null ? props.skip : false});

    useEffect(() => {
        addValues();
    }, []);

    useEffect(() => {
        if (contractTypesQuery.data) {
            const contractTypesTemp = contractTypesQuery.data.map((x: any) => {
                return {label: x.name, value: x.id};
            });

            setColumns(getColumnsContract(t, contractTypesTemp,));
        }
    }, [contractTypesQuery]);

    useEffect(() => {
        setIsDirty(props.isDirty);
    }, [props.isDirty]);

    // useEffect(() => {
    //     if (checkPermission(permissions.editImportData)) {
    //         setCustomButtons([{
    //             icons: [{key: null, value: null, icon: <FontAwesomeIcon icon={faMap}/>}],
    //             tooltip: t('edit'),
    //             tooltipOptions: {position: 'top'},
    //             onClick: (data: any) => {
    //                 setIsDirty((prevState: boolean) => {
    //                     const url = `/project/${props.projectId}/#globalData_2_${data['flurstueck_id']}`;
    //                     if (prevState) {
    //                         CustomConfirmDialog({
    //                             message: t('dialogs.saveChangesMessage'),
    //                             header: t('dialogs.saveChangesHeader'),
    //                             translation: t,
    //                             onConfirm: () => {
    //                                 navigate(url);
    //                             },
    //                             rejectClassName: 'float-right p-button-secondary'
    //                         });
    //                     } else {
    //                         navigate(url);
    //                     }
    //                     return prevState;
    //                 });
    //             },
    //             permitted: true,
    //         }]);
    //     } else {
    //         setCustomButtons([]);
    //     }
    // }, [props.mappingType]);


    useEffect(() => {
        if (contracts) {
            setValues(contracts);
            setDataLoaded(true);
            setActiveMappings(contracts ? contracts.map((item: Contract) => {
                return item.id;
            }) : []);
        }
    }, [isSuccess]);

    useEffect(() => {
        addValues();
    }, [JSON.stringify(mappings)]);

    // useEffect(() => {
    //     if (selectedValue && props.mappingType != null) {
    //         setShowMappingEdit(true);
    //         setMappingDialog(getMappingContent());
    //     }
    // }, [selectedValue]);

    useEffect(() => {
        if (props.mappingId === '') {
            setValues([]);
        }
    }, [props.mappingId]);

    useEffect(() => {
        if (selectedValue && props.mappingType != null) {
            setShowMappingEdit(true);
            setMappingDialog(getMappingContent(props.projectId, selectedValue, 'flurstueck_id', props.mappingId, props.mappingType, setMappingInputs, setHasValidationError));
        }
    }, [selectedValue]);

    const addValues = () => {
        setValues((prevState: any) => {
            const temp = [...prevState];

            if (temp) {
                for (let i = 0; i < mappings.length; i++) {
                    const map = mappings[i];

                    if (map) {
                        const item = temp.find((x: Contract) => x.vertrag_type_id === map.id);

                        if (item) {
                            if (map.remove) {
                                temp.splice(temp.indexOf(item), 1);
                            }
                        } else {
                            if (!map.remove) {
                                const tempItem = contracts.find((x: Contract) => x.vertrag_type_id === map.id);
                                if (tempItem) {
                                    temp.push({...tempItem, ...map});
                                }
                            }
                        }
                    }
                }
            }
            return temp;
        });
    };

    const clickAddMapping = (e: any) => {
        setShowMappingAdd(true);
    };

    const onHide = () => {
        setShowMappingAdd(false);
        setSelectedValue(undefined);
    };

    const onAddMapping = (selectedItem: any, mapping: any) => {
        const vertrag_type_id = mapping.vertrag_type_id;
        const datum_soll = mapping.datum_soll;
        const datum_ist = mapping.datum_ist;
        const flaeche_erwerb = mapping.flaeche_erwerb;
        const flaeche_dinglich = mapping.flaeche_dinglich;
        const flaeche_temp = mapping.flaeche_temp;

        let tempM = {
            id: vertrag_type_id,
            mapping_type: props.mappingType,
            project_id: props.projectId,
            common_data: true,
            vertrag_type_id: vertrag_type_id,
            datum_soll: datum_soll,
            datum_ist: datum_ist,
            flaeche_erwerb: flaeche_erwerb,
            flaeche_dinglich: flaeche_dinglich,
            flaeche_temp: flaeche_temp
        };

        dispatch(addMappings(tempM));

        setValues((prevState: any) => {
            const temp = [...prevState];

            temp.push(tempM);
            return temp;
        });

        setShowMappingAdd(false);

        props.onChange();
    };

    const assignMapping = () => {
        assignMappingGlobal('vertrag_type_id', selectedValue, mappingInputs, props.mappingType, dispatch, setValues);
        props.onChange();
        hideDialog();
    };

    const hideDialog = () => {
        setShowMappingAdd(false);
        setShowMappingEdit(false);
        setSelectedValue(undefined);
        setMappingInputs({});
    };

    return (<div className={props.className}>
        <CustomDataTable
            id="ContractDataTable_mapping"
            header={
                <div className={'flex'}>
                    {<>
                        {props.showHeader && <h4
                            className={'m-0'}>{t('globalData:global.flurstuecke')}</h4>}
                        <div className={'w-full grid grid-nogutter justify-content-end'}>
                            <Button
                                className="p-button-success p-button-outlined p-button-icon-only mr-3 mb-3 xl:mb-0"
                                type="button" icon="pi pi-plus"
                                onClick={clickAddMapping}/>
                        </div>
                    </>}
                </div>
            }
            className={props.className}
            columns={columns}
            customButtons={customButtons}
            scrollHeight={props.scrollHeight}
            sortable={true}
            value={values}
            paginator={true}
            paginatorHideAll={true}
            displayColumnFilter={true}
            editable={checkPermission(permissions.editContracts)}
            onEditClick={(data: any) => {
                console.log('data', data);
                setSelectedValue(data);
            }}
            ableToDelete={checkPermission(permissions.editContracts)}
            onRemoveClick={(e) => {
                CustomConfirmDialog({
                    message: t('globalData:global.mapping.removeMappingContract'),
                    header: t('confirmDialog:deleteDialogHeader'),
                    translation: t,
                    onConfirm: () => {
                        if (props.mappingType) {
                            const item: Mapping = {
                                id: e.vertrag_type_id,
                                vertrag_type_id: e.vertrag_type_id,
                                flurstueck_id: props.mappingId,
                                mapping_type: props.mappingType,
                                project_id: props.projectId,
                                common_data: true,
                                remove: true
                            };

                            switch (props.mappingType) {
                                case MappingType.parcelContract:
                                    item.flurstueck_id = props.mappingId;
                                    break;
                            }

                            dispatch(removeMapping(item));
                            props.onChange();
                        }
                    }
                });
            }}
            ableToUpdate={checkPermission(permissions.editContracts)}
            filters={getFiltersContract()}
            dataloaded={dataLoaded}
            hideGlobalFilter={false}
            skeletonCount={1}
        />
        {showMappingAdd &&
            <CustomDialog className={'mapping-dialog'}
                          header={t('globalData:global.mapping.header')}
                          onCancel={hideDialog}
                          visible={showMappingAdd}
                          maximized={false}>
                <OverlayAddMapping id={props.mappingId}
                                   projectId={props.projectId}
                                   id_type={'contract'}
                                   type={props.mappingType}
                                   activeMappings={values ? values.map((item: Contract) => {
                                       return item.vertrag_type_id;
                                   }) : []}
                                   onSubmit={onAddMapping}/>
            </CustomDialog>
        }
        {showMappingEdit && <CustomDialog
            className={'mapping-dialog'}
            header={t('globalData:global.mapping.header')}
            onCancel={hideDialog}
            visible={showMappingEdit}
            maximized={false}
            onClick={assignMapping}>
            {mappingDialog}
        </CustomDialog>}
    </div>);
};
